import { api } from '../api/client';

export interface ContestSettings {
  contact_emails: string[];
  captcha_configured: boolean;
  updated_at?: string;
}

export interface ContestOptions {
  captcha_configured: boolean;
  contacts_configured: boolean;
  cloudflare_configured: boolean;
}

export interface ContestRun {
  id: string;
  label: string;
  status: 'queued' | 'running' | 'completed' | 'needs_attention';
  created_at: string;
  created_by: string;
  total_count: number;
  submitted_count: number;
  attention_count: number;
  active_count: number;
  cf_account_id?: number | null;
  finished_at: string | null;
  /** Manual DNS records the team still has to add (run list only). */
  records_waiting_count?: number;
}

export type ContestDnsMode = 'cloudflare' | 'manual';

export interface ContestItem {
  id: string;
  domain: string;
  status: 'queued' | 'case_created' | 'cname_written' | 'waiting_verify' | 'submit_unconfirmed' | 'submitted' | 'needs_human' | 'failed';
  contact_email: string;
  case_id: string | null;
  ownership_url: string | null;
  attempts_create: number;
  attempts_submit: number;
  last_error: string | null;
  submitted_at: string | null;
  updated_at: string;
  worker_id?: string;
  case_creation_inflight: boolean;
  dns_mode: ContestDnsMode;
  cname_at: string | null;
  /** Set when the domain was moved to a newer run; the worker stops on it. */
  released_at: string | null;
}

/** Typed by the operator to contest domains that are still on our panels. */
export const MANAGED_CONFIRMATION = 'ACCEPT REMOVE FROM OUR INFRA';

/** Typed by the operator to take domains out of an earlier run and contest them again. */
export const RESTART_CONFIRMATION = 'RESTART CONTEST';

export interface ExistingContestDomain {
  domain: string;
  run_id: string;
  run_name: string;
  status: ContestItem['status'];
  case_id: string | null;
}

export interface ManagedContestDomain {
  domain: string;
  panels: string[];
}

export interface ContestDetail {
  run: ContestRun;
  items: ContestItem[];
}

export const contestApi = {
  options: () => api<ContestOptions>('/api/contest/options'),
  runs: () => api<{ runs: ContestRun[] }>('/api/contest/runs'),
  create: (input: {
    label: string; domains: string[]; dns_mode: ContestDnsMode;
    managed_confirmation?: string; accept_managed_domains?: string[];
    restart_confirmation?: string; restart_domains?: string[];
  }) =>
    api<ContestRun>('/api/contest/runs', { method: 'POST', body: JSON.stringify(input) }),
  detail: (id: string) => api<ContestDetail>(`/api/contest/runs/${encodeURIComponent(id)}`),
  retry: (id: string) => api(`/api/contest/runs/${encodeURIComponent(id)}/retry`, { method: 'POST' }),
  recoverCase: (id: string, itemId: string, ownership_url: string) =>
    api(`/api/contest/runs/${encodeURIComponent(id)}/items/${encodeURIComponent(itemId)}/case`, {
      method: 'POST', body: JSON.stringify({ ownership_url }),
    }),
  settings: () => api<ContestSettings>('/api/settings/contest'),
  saveSettings: (input: { contact_emails: string[]; captcha_api_key?: string }) =>
    api<ContestSettings>('/api/settings/contest', { method: 'PATCH', body: JSON.stringify(input) }),
};

export function contestError(error: unknown): string {
  return error instanceof Error ? error.message : 'The request failed. Please try again.';
}
